import { StatusBadge } from "@components/display/StatusBadge";
import { BLOC_LABEL, MODE_LABEL, TYPE_LABEL } from "@features/pointage/config/constants";
import { POINTAGE_STATUT } from "@features/pointage/config/statuses";
import useGeolocation from "@features/pointage/hooks/useGeolocation";
import { usePointageContext, usePointageSelf } from "@features/pointage/hooks/usePointageContext";
import type { ModePointage, PointageSlot } from "@features/pointage/types/pointageSelf";
import { useToast } from "@lib/hooks/useToast";
import { cn } from "@lib/utils/cn";
import { Clock, Loader2, Lock, MapPin } from "lucide-react";
import { useState } from "react";
import { DemiJourneeSelector } from "./DemiJourneeSelector";

/** Modes where the employee pointers themselves on the home dashboard (PC/VARIABLE; DEMI_JOURNEE = selector). */
const SELF_POINT_MODES = new Set<ModePointage>(["PC", "VARIABLE"]);

/** Mirrors backend ToleranceService::MAX_MATCH_WINDOW_MINUTES — outside this window a pointage is HORS_CRENEAU. */
const MAX_MATCH_WINDOW_MS = 240 * 60 * 1000;

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatTime(iso: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatDateShort(iso: string): string {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

/**
 * PointageWidget — self-service "Pointer" tile for the home dashboard.
 * Renders only for PC / VARIABLE modes (modePointage from the backend context).
 */
export function PointageWidget() {
  const { data, isLoading } = usePointageContext({ date: toISODate(new Date()) });
  const { mutate, isPending } = usePointageSelf();
  const location = useGeolocation();
  const toast = useToast();
  const [pendingSlot, setPendingSlot] = useState<string | null>(null);

  if (isLoading || !data || data.mode === null) return null;
  if (data.mode === "DEMI_JOURNEE") {
    return (
      <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden">
        <div className="p-4 md:p-5 flex items-center justify-between gap-3 border-b border-base-200/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-warning/10 text-warning flex items-center justify-center shrink-0 shadow-sm">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <h3 className="card-title text-sm font-bold mb-0">Pointage · Demi-journée</h3>
              <p className="text-xs text-base-content/60 mt-0.5">{formatDateShort(data.date)}</p>
            </div>
          </div>
          <span className="badge badge-ghost badge-sm text-base-content/60">
            {MODE_LABEL[data.mode]}
          </span>
        </div>
        <DemiJourneeSelector />
      </div>
    );
  }
  if (!SELF_POINT_MODES.has(data.mode)) return null;

  const handlePointerSlot = async (slot: PointageSlot) => {
    try {
      const position = await location.getCurrentPosition();
      if (!position || position.latitude === 0 || position.longitude === 0) {
        toast.error("Position GPS non fiable. Réessayez.");
        return;
      }
      setPendingSlot(`${slot.bloc}-${slot.type}`);
      mutate(
        {
          bloc: slot.bloc,
          type: slot.type,
          latitude: position.latitude,
          longitude: position.longitude,
          accuracy: position.accuracy,
        },
        {
          onSettled: () => setPendingSlot(null),
        },
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Géolocalisation impossible");
    }
  };

  return (
    <div className="card bg-base-200 shadow-sm border border-base-300/60 rounded-xl overflow-hidden">
      <div className="p-4 md:p-5 flex items-center justify-between gap-3 border-b border-base-200/60">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-warning/10 text-warning flex items-center justify-center shrink-0 shadow-sm">
            <Clock className="w-5 h-5" />
          </div>
          <div>
            <h3 className="card-title text-sm font-bold mb-0">Pointage du jour</h3>
            <p className="text-xs text-base-content/60 mt-0.5">{formatDateShort(data.date)}</p>
          </div>
        </div>
        <span className="badge badge-ghost badge-sm text-base-content/60">
          {MODE_LABEL[data.mode]}
        </span>
      </div>

      <div className="divide-y divide-base-200/40">
        {data.expectedSlots.length > 0 ? (
          data.expectedSlots.map((slot) => {
            const pointage = data.pointages.find(
              (p) => p.bloc === slot.bloc && p.type === slot.type,
            );
            const inWindow =
              Math.abs(new Date(slot.expectedAt).getTime() - Date.now()) <= MAX_MATCH_WINDOW_MS;
            const arrivalDone = data.pointages.some(
              (p) => p.bloc === slot.bloc && p.type === "ARRIVEE",
            );
            const departBlocked = slot.type === "DEPART" && !arrivalDone;
            return (
              <div
                key={`${slot.bloc}-${slot.type}`}
                className="px-4 py-2.5 flex items-center justify-between gap-3"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-base-content">
                    {BLOC_LABEL[slot.bloc]} · {TYPE_LABEL[slot.type]}
                  </p>
                  <p className="text-xs text-base-content/60 truncate">
                    {pointage
                      ? `Pointé à ${formatTime(pointage.time)}`
                      : `Attendu à ${formatTime(slot.expectedAt)}`}
                  </p>
                </div>
                {pointage?.statut ? (
                  <StatusBadge status={pointage.statut} config={POINTAGE_STATUT} size="sm" />
                ) : !inWindow ? (
                  <span className="badge badge-ghost badge-sm text-base-content/50">
                    Non pointé
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handlePointerSlot(slot)}
                    disabled={isPending || departBlocked}
                    title={
                      departBlocked
                        ? `Pointez d'abord l'arrivée du ${BLOC_LABEL[slot.bloc]}`
                        : undefined
                    }
                    className={cn(
                      "btn btn-primary btn-xs gap-1.5",
                      (isPending || departBlocked) && "btn-disabled pointer-events-none",
                    )}
                  >
                    {pendingSlot === `${slot.bloc}-${slot.type}` ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : departBlocked ? (
                      <Lock className="w-3.5 h-3.5" />
                    ) : (
                      <MapPin className="w-3.5 h-3.5" />
                    )}
                    Pointer
                  </button>
                )}
              </div>
            );
          })
        ) : (
          <p className="px-4 py-3 text-sm text-base-content/50">
            Aucun créneau attendu aujourd'hui.
          </p>
        )}
      </div>
    </div>
  );
}
